import { useState } from 'react';
import _ from 'lodash';

import * as api from '../../../../api';

import { styled } from 'styled-components';

import { Col, Row } from 'react-bootstrap';

import { Link, Typography, VStack } from '@vivaahealth/design-system';
import { StyledBorder, StyledHeaderSmall } from '../Common';
import { hasBackgroundInfo } from '../Common/utils';

const ABOUT_LETTER_LIMIT = 520;

interface Props {
  provider: api.PrevisitProvider.SafeProviderWithPractices;
  aboutRef?: React.RefObject<HTMLDivElement>;
}

const ProviderInfo: React.FC<Props> = ({ provider, aboutRef }) => {
  const [showMoreAbout, setShowMoreAbout] = useState(false);
  const [showMoreCarePhilosophy, setShowMoreCarePhilosophy] = useState(false);
  const [showMoreAreasOfInterest, setShowAreasOfInterest] = useState(false);

  const hiddenTextControl = (text?: string) => {
    if (!text || text.length <= ABOUT_LETTER_LIMIT) {
      return false;
    }
    return true;
  };
  const hasAbout =
    !!provider.profile?.about && provider.profile?.about.length > 0;
  const hasCarePhilosophy =
    !!provider.profile?.carePhilosophy &&
    provider.profile?.carePhilosophy.length > 0;
  const hasAreasOfInterest =
    !!provider.profile?.areasOfInterest &&
    provider.profile?.areasOfInterest.length > 0;
  const hasPersonalInfo =
    !!hasAbout || !!hasCarePhilosophy || !!hasAreasOfInterest;
  const hasVideoUrl = !!provider.preference?.videoUrl;
  const {
    hasAwards,
    hasCertifications,
    hasEducation,
    hasPositions,
    hasProfessionalMemberships,
    hasSex,
  } = hasBackgroundInfo(provider);
  const showFirstColumn = hasEducation || hasCertifications || hasPositions;
  const showSecondColumn = hasAwards || hasProfessionalMemberships || hasSex;

  const providerInformation = () => {
    return (
      <>
        {hasAbout && (
          <PreWrapParagraphLarge variant="paragraph-large">
            <Typography variant="paragraph-large" bold>
              Biography
            </Typography>
            <Typography variant="paragraph-large">
              {hasAbout ? (
                showMoreAbout ? (
                  provider.profile?.about
                ) : (
                  provider.profile?.about!.substring(0, ABOUT_LETTER_LIMIT)
                )
              ) : (
                <></>
              )}
              <StyledLink
                className="link-secondary ps-1"
                onClick={() => setShowMoreAbout((showMore) => !showMore)}
                small
              >
                {hiddenTextControl(provider.profile?.about)
                  ? showMoreAbout
                    ? 'Show less'
                    : '...Show more'
                  : ''}
              </StyledLink>
            </Typography>
          </PreWrapParagraphLarge>
        )}
        {hasCarePhilosophy && (
          <PreWrapParagraphLarge variant="paragraph-large">
            <Typography className="pb-1" variant="paragraph-large" bold>
              Care Philosophy
            </Typography>
            <Typography variant="paragraph-large">
              {hasCarePhilosophy ? (
                showMoreCarePhilosophy ? (
                  provider.profile?.carePhilosophy
                ) : (
                  provider.profile?.carePhilosophy!.substring(
                    0,
                    ABOUT_LETTER_LIMIT,
                  )
                )
              ) : (
                <></>
              )}
              <StyledLink
                className="link-secondary ps-1"
                onClick={() =>
                  setShowMoreCarePhilosophy((showMore) => !showMore)
                }
                small
              >
                {hiddenTextControl(provider.profile?.carePhilosophy)
                  ? showMoreCarePhilosophy
                    ? 'Show less'
                    : '...Show more'
                  : ''}
              </StyledLink>
            </Typography>
          </PreWrapParagraphLarge>
        )}
        {hasAreasOfInterest && (
          <PreWrapParagraphLarge variant="paragraph-large">
            <Typography className="pb-1" variant="paragraph-large" bold>
              Areas of Interest
            </Typography>
            <Typography variant="paragraph-large">
              {hasAreasOfInterest ? (
                showMoreAreasOfInterest ? (
                  provider.profile?.areasOfInterest
                ) : (
                  provider.profile?.areasOfInterest!.substring(
                    0,
                    ABOUT_LETTER_LIMIT,
                  )
                )
              ) : (
                <></>
              )}
              <StyledLink
                className="link-secondary ps-1"
                onClick={() => setShowAreasOfInterest((showMore) => !showMore)}
                small
              >
                {hiddenTextControl(provider.profile?.areasOfInterest)
                  ? showMoreAreasOfInterest
                    ? 'Show less'
                    : '...Show more'
                  : ''}
              </StyledLink>
            </Typography>
          </PreWrapParagraphLarge>
        )}
      </>
    );
  };

  const about = () => {
    if (!hasPersonalInfo) {
      return null;
    }
    return (
      <VStack gap={16}>
        <Typography variant="headline-small" bold>
          Provider Information
        </Typography>
        {hasVideoUrl ? (
          <Row className="gx-4">
            <Col lg={6}>
              <VStack gap={16}>{providerInformation()}</VStack>
            </Col>
            <Col lg={6}>
              <EmbeddedVideo
                src={`${provider.preference?.videoUrl}`}
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                title="Embedded youtube"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            </Col>
          </Row>
        ) : (
          providerInformation()
        )}
      </VStack>
    );
  };

  const education = () => {
    if (!hasEducation) {
      return null;
    }
    return (
      <>
        <div className="mb-1">
          <Typography variant="paragraph-large" bold>
            Education
          </Typography>
        </div>
        <ul>
          {provider.profile?.education?.map((e, i) => (
            <li key={`${e.institution}${i}`}>
              <Typography variant="paragraph-large">
                {api.ProviderProfile.EducationKindMap[e.kind] + ': '}
                {e.institution}
                {e.yearEnded ? `, ${e.yearEnded}` : ''}
              </Typography>
            </li>
          ))}
        </ul>
      </>
    );
  };

  const positions = () => {
    if (!hasPositions) {
      return null;
    }
    return (
      <>
        <div className="mb-1">
          <Typography variant="paragraph-large" bold>
            Teaching and Professional Positions
          </Typography>
        </div>
        <ul>
          {provider.profile?.positions?.map((position, i) => (
            <li key={`${position.name}${i}`}>
              <Typography variant="paragraph-large">
                {position.organization ? `${position.organization}, ` : ''}
                {position.location ? `${position.location}, ` : ''}
                {position.name}
                {position.years ? `, ${position.years}` : ''}
              </Typography>
            </li>
          ))}
        </ul>
      </>
    );
  };

  const certifications = () => {
    if (!hasCertifications) {
      return null;
    }
    return (
      <>
        <div className="mb-1 mt-4">
          <Typography variant="paragraph-large" bold>
            Certifications
          </Typography>
        </div>
        <ul>
          {provider.profile?.certifications?.map((c, i) => (
            <li key={`${c.certification}${i}`}>
              <Typography variant="paragraph-large">
                {c.certification}
                {c.institution ? ` - ${c.institution}` : ''}
                {c.year ? `, ${c.year}` : ''}
              </Typography>
            </li>
          ))}
        </ul>
      </>
    );
  };

  const awards = () => {
    if (!hasAwards) {
      return null;
    }
    return (
      <>
        <Typography variant="paragraph-large" className="mb-1 mt-4" bold>
          Awards
        </Typography>
        <ul>
          {provider.profile?.awards?.map((a, i) => (
            <li key={`${a.award}${i}`}>
              <Typography variant="paragraph-large">
                {a.award}
                {a.sponsor ? ` - ${a.sponsor}` : ''}
                {a.year ? `, ${a.year}` : ''}
              </Typography>
            </li>
          ))}
        </ul>
      </>
    );
  };

  const professionalMemberships = () => {
    if (!hasProfessionalMemberships) {
      return null;
    }
    return (
      <>
        <div className="md-mt-4">
          <Typography variant="paragraph-large" bold>
            Professional Memberships
          </Typography>
        </div>
        <ul>
          {provider.profile?.professionalMemberships?.map((membership, i) => (
            <li key={`${membership}${i}`}>
              <Typography variant="paragraph-large">
                {membership.name}
              </Typography>
            </li>
          ))}
        </ul>
      </>
    );
  };

  const sex = () => {
    if (!hasSex) {
      return null;
    }
    return (
      <>
        <div className="mt-4">
          <Typography variant="paragraph-large" bold>
            Gender
          </Typography>
        </div>
        <Typography variant="paragraph-large">
          {_.upperFirst(provider.sex ?? '')}
        </Typography>
      </>
    );
  };

  const hasContent = showFirstColumn || showSecondColumn;

  const content = hasContent && (
    <>
      <StyledHeaderSmall variant="headline-small" bold>
        Credentials and Background
      </StyledHeaderSmall>
      <Row xs={1} md={2} className="me-0 gx-5">
        {showFirstColumn && (
          <Col>
            {education()}
            {positions()}
            {certifications()}
            {awards()}
          </Col>
        )}
        {showSecondColumn && (
          <Col>
            {professionalMemberships()}
            {sex()}
          </Col>
        )}
      </Row>
    </>
  );

  return (
    <>
      {hasPersonalInfo && <div ref={aboutRef}>{about()}</div>}
      {hasPersonalInfo && hasContent && <StyledBorder />}
      {hasContent && <div ref={aboutRef}>{content}</div>}
    </>
  );
};

export default ProviderInfo;

const PreWrapParagraphLarge = styled(Typography)`
  white-space: pre-wrap;
`;

const StyledLink = styled(Link).attrs<{ color: string }>((props) => ({
  color: props.color || 'var(--color-primary)',
}))`
  color: ${(props) => props.color} !important;
  cursor: pointer;
  text-decoration: none;
  font-weight: normal;
`;

const EmbeddedVideo = styled.iframe`
  aspect-ratio: 16/9;
  width: 100%;
  @media (max-width: 991px) {
    margin-top: 36px;
  }
`;
